import type { ReactElement } from "react";
import { cn } from "../../lib/utils";
import { RANGS, type RangId } from "../contract";
import { nomRang } from "./viewers-texts";

/**
 * Le blason d'un rang : son nom ecrit, son insigne (autant de losanges que le rang a de marches : un Ecuyer en a un,
 * une Legende six) et sa couleur. Le rang se lit donc sans la couleur : le nom est du texte, le nombre de losanges
 * est dans la forme. Deux rangs ne partagent jamais ni l'un ni l'autre.
 *
 * Contrastes, mesures par `rang.test.tsx` contre le fond de la regie (`--card`) : le texte est toujours la couleur
 * du texte de la carte (13:1), jamais la couleur du rang, dont deux (Ecuyer, Seigneur) tiennent moins de 3:1 sur ce
 * fond. Le losange est rempli de la couleur du rang mais cerne du ton clair du texte, qui tient 13:1 : c'est le
 * contour qui porte le contraste, pas le remplissage. Le liseré du blason, lui, est decoratif.
 */

/** Le ton du texte et du contour des insignes : celui de `--card-foreground` (`theme.css`). */
export const TON_CLAIR = "#f6ebd9";

const COULEURS: Readonly<Record<RangId, string>> = Object.fromEntries(
  RANGS.map((rang) => [rang.id, rang.couleur]),
) as Record<RangId, string>;

/** Le nombre de losanges d'un rang : sa place dans l'echelle, a partir de un. */
export function nombreDeLosanges(id: RangId): number {
  return RANGS.findIndex((rang) => rang.id === id) + 1;
}

function Insigne({ id }: { id: RangId }): ReactElement {
  const n = nombreDeLosanges(id);
  return (
    <svg
      aria-hidden="true"
      data-testid="insigne"
      data-losanges={n}
      width={n * 14 + 2}
      height={18}
      viewBox={`0 0 ${n * 14 + 2} 18`}
      className="flex-none"
    >
      {RANGS.slice(0, n).map((marche, i) => (
        <path
          key={marche.id}
          d={`M${i * 14 + 8} 2 L${i * 14 + 14} 9 L${i * 14 + 8} 16 L${i * 14 + 2} 9 Z`}
          fill={COULEURS[id]}
          stroke={TON_CLAIR}
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}

export function Rang({ id, className }: { id: RangId; className?: string }): ReactElement {
  return (
    <span
      data-rang={id}
      style={{ borderColor: COULEURS[id] }}
      className={cn(
        "police-texte inline-flex items-center gap-2 rounded-[4px] border-2 bg-background px-2.5 py-0.5 text-[20px] leading-tight text-card-foreground",
        className,
      )}
    >
      <Insigne id={id} />
      <span>{nomRang(id)}</span>
    </span>
  );
}
